"use strict";
import Account from "../api/account";
import uuid from "@lukeed/uuid";
const DOC_VIEWER_POPOUT_NAME = "DocViewerPopout";
const DOCUMENT_VIEWER_OPEN_LOCAL_STORAGE_KEY = "docViewerOpen";
const DOCUMENT_PAGE_LINE_FROM_POPOUT_KEY = "docPageLineFromPopout";
const SELECTED_FIELD_FROM_MAIN_PAGE_KEY = "selectedFieldFromMainPage";
const HOVERED_TEXT_FROM_MAIN_PAGE_KEY = "hoveredTextFromMainPage";
const CURRENT_PROCESSING_SELECTION_KEY = "currentProcessingSelection";
const CURRENT_PROCESSING_SELECTION_SOURCE_MAIN_VIEWER = 0;
const CURRENT_PROCESSING_SELECTION_SOURCE_POPOUT = 1;
const REFRESH_KEY = "currentProcessingSelectionShouldRefresh";
const VIEW_EMAIL_FORWARD_DATA_KEY = "viewEmailForwardData";
const DOCUMENT_POD_UPDATES_KEY = "documentUpdatePOD";
const DOCUMENT_VIEWER_PAGE_CONTEXT_DATA_KEY = "documentViewerPageContextData";
const popoutToggleListeners = {};
const popoutWindowRefs = {};
const isAccountEnabled = function() {
  return Account.hasRole("Broker") || Account.useDocumentViewerPopout();
};
const isDocumentViewerPopoutEnabled = function() {
  return Account.hasRole("Broker") || Account.useDocumentViewerPopout();
};
const isDocumentViewerOpen = function() {
  return isDocumentViewerPopoutEnabled() && window.localStorage.getItem(DOCUMENT_VIEWER_OPEN_LOCAL_STORAGE_KEY) === "true";
};
const openDocumentViewer = function() {
  if (isDocumentViewerPopoutEnabled()) {
    window.localStorage.setItem(DOCUMENT_VIEWER_OPEN_LOCAL_STORAGE_KEY, "true");
    Object.values(popoutToggleListeners).forEach((listener) => {
      listener();
    });
  }
};
const closeDocumentViewer = function() {
  if (isDocumentViewerPopoutEnabled()) {
    window.localStorage.setItem(
      DOCUMENT_VIEWER_OPEN_LOCAL_STORAGE_KEY,
      "false"
    );
    Object.values(popoutToggleListeners).forEach((listener) => {
      listener();
    });
  }
};
const setViewEmailForwardData = function(document) {
  if (isAccountEnabled() && isDocumentViewerOpen()) {
    window.localStorage.setItem(VIEW_EMAIL_FORWARD_DATA_KEY, document);
  }
};
const setFieldSelection = function(identifier) {
  if (isAccountEnabled() && isDocumentViewerOpen()) {
    window.localStorage.setItem(SELECTED_FIELD_FROM_MAIN_PAGE_KEY, identifier);
  }
};
const isSelectedField = function(identifier) {
  return identifier == window.localStorage.getItem(SELECTED_FIELD_FROM_MAIN_PAGE_KEY);
};
const setHoveredText = function(hoveredText) {
  if (isAccountEnabled() && hoveredText) {
    window.localStorage.setItem(HOVERED_TEXT_FROM_MAIN_PAGE_KEY, hoveredText);
  } else {
    window.localStorage.removeItem(HOVERED_TEXT_FROM_MAIN_PAGE_KEY);
  }
};
const clearDocumentPageLine = function() {
  window.localStorage.removeItem(DOCUMENT_PAGE_LINE_FROM_POPOUT_KEY);
};
const registerOnPopoutToggleListener = function(name, callback) {
  if (isAccountEnabled()) {
    popoutToggleListeners[name] = callback;
    window.addEventListener(
      "storage",
      (event) => {
        if (event.key === DOCUMENT_VIEWER_OPEN_LOCAL_STORAGE_KEY) {
          callback();
        }
      },
      true
    );
  }
};
const unregisterOnPopoutToggleListener = function(name) {
  delete popoutToggleListeners[name];
};
const setupDocPageLineFromPopoutListener = function(callback) {
  if (isAccountEnabled()) {
    const identifier = uuid();
    window.addEventListener("storage", (event) => {
      if (event.key == DOCUMENT_PAGE_LINE_FROM_POPOUT_KEY && isSelectedField(identifier)) {
        var json = window.localStorage.getItem(
          DOCUMENT_PAGE_LINE_FROM_POPOUT_KEY
        );
        if (json) {
          var line = JSON.parse(json);
          if (line) {
            callback(line);
            clearDocumentPageLine();
          }
        }
      }
    });
    return identifier;
  }
};
const setCurrentProcessingSelection = function(documentBatch, documentGroup, documentPage, isConfirmAndSubmit, maybeCarrierInvoiceId) {
  if (isConfirmAndSubmit) {
    if (documentGroup) {
      const carrierInvoiceId = maybeCarrierInvoiceId || documentGroup.documents[0].carrier_invoice_id;
      setCarrierInvoiceId(carrierInvoiceId, documentPage.id);
    } else {
      setEmptySelection();
    }
  } else {
    if (!!documentBatch && !!documentGroup) {
      setDocumentBatchAndGroupAndPageId(
        documentBatch.id,
        documentGroup.id,
        documentPage.id
      );
    } else if (documentBatch) {
      setDocumentBatchAndGroupAndPageId(documentBatch.id, "", documentPage.id);
    } else {
      setEmptySelection();
    }
  }
};
const refreshPopout = function() {
  if (isAccountEnabled()) {
    window.localStorage.setItem(REFRESH_KEY, Date.now().toString());
  }
};
const setCarrierInvoiceId = function(carrierInvoiceId, documentPageId) {
  if (isAccountEnabled()) {
    storeCurrentProcessingSelection({
      carrierInvoiceId,
      documentPageId
    });
  }
};
const setCustomerId = function(customerId) {
  const key = CURRENT_PROCESSING_SELECTION_KEY;
  let currentRecord;
  try {
    var json = window.localStorage.getItem(key);
    if (json) {
      currentRecord = JSON.parse(json);
    } else {
      currentRecord = {};
    }
  } catch (e) {
    currentRecord = {};
  }
  currentRecord.customerId = customerId;
  currentRecord.customerIdSetAt = Date.now();
  const newRecord = JSON.stringify(currentRecord);
  window.localStorage.setItem(key, newRecord);
};
const setDocumentBatchAndGroupAndPageId = function(documentBatchId, documentGroupId, documentPageId) {
  if (isAccountEnabled()) {
    storeCurrentProcessingSelection({
      documentBatchId,
      documentGroupId,
      documentPageId
    });
  }
};
const setSinglePageId = function(documentPageId) {
  if (isAccountEnabled()) {
    storeCurrentProcessingSelection({ documentPageId });
  }
};
const setEmptySelection = function() {
  if (isAccountEnabled()) {
    storeCurrentProcessingSelection(null);
  }
};
const storeCurrentProcessingSelection = function(selection) {
  const source = CURRENT_PROCESSING_SELECTION_SOURCE_MAIN_VIEWER;
  const newValue = JSON.stringify([source, selection]);
  window.localStorage.setItem(CURRENT_PROCESSING_SELECTION_KEY, newValue);
};
const openOrFocus = function(url, target, windowFeatures) {
  const ref = popoutWindowRefs[target];
  if (!ref || ref.closed) {
    popoutWindowRefs[target] = window.open(url, target, windowFeatures);
  } else {
    ref.focus();
  }
};
function openOrFocusBillingReqsPopout() {
  openOrFocus(
    "/app/billing_requirements/",
    "BillingReqsPopout",
    "width=920,height=800"
  );
}
const setDocumentPodUpdate = function(documentPodUpdate) {
  if (isAccountEnabled()) {
    window.localStorage.setItem(
      DOCUMENT_POD_UPDATES_KEY,
      JSON.stringify(documentPodUpdate)
    );
  }
};
const setPageContextData = function(data) {
  if (isAccountEnabled()) {
    const jsonData = JSON.stringify(data);
    const key = DOCUMENT_VIEWER_PAGE_CONTEXT_DATA_KEY;
    window.localStorage.setItem(key, jsonData);
  }
};
const getMaybeDocumentPodUpdate = function() {
  const json = window.localStorage.getItem(
    DOCUMENT_POD_UPDATES_KEY
  );
  const update = JSON.parse(json || "null");
  return update;
};
export {
  DOC_VIEWER_POPOUT_NAME,
  HOVERED_TEXT_FROM_MAIN_PAGE_KEY,
  DOCUMENT_VIEWER_OPEN_LOCAL_STORAGE_KEY,
  DOCUMENT_PAGE_LINE_FROM_POPOUT_KEY,
  CURRENT_PROCESSING_SELECTION_KEY,
  CURRENT_PROCESSING_SELECTION_SOURCE_MAIN_VIEWER,
  CURRENT_PROCESSING_SELECTION_SOURCE_POPOUT,
  REFRESH_KEY,
  VIEW_EMAIL_FORWARD_DATA_KEY,
  DOCUMENT_POD_UPDATES_KEY,
  DOCUMENT_VIEWER_PAGE_CONTEXT_DATA_KEY,
  isAccountEnabled,
  isDocumentViewerOpen,
  isDocumentViewerPopoutEnabled,
  openDocumentViewer,
  closeDocumentViewer,
  setCurrentProcessingSelection,
  setCarrierInvoiceId,
  setCustomerId,
  setDocumentBatchAndGroupAndPageId,
  setSinglePageId,
  setEmptySelection,
  setFieldSelection,
  setHoveredText,
  setViewEmailForwardData,
  setupDocPageLineFromPopoutListener,
  setPageContextData,
  refreshPopout,
  openOrFocus,
  openOrFocusBillingReqsPopout,
  registerOnPopoutToggleListener,
  unregisterOnPopoutToggleListener,
  setDocumentPodUpdate,
  getMaybeDocumentPodUpdate
};
